/**
 * Das **Gerät einer Erzeugungsanlage** in der Oberfläche (HM-129, ADR 0038
 * Nachtrag): welches Gerät zu welcher Art passt, und wie sein Zustand in einer
 * Zeile steht.
 *
 * **Die Regel ist eine Abschrift** von `generationAssetDeviceProblem` in
 * `@hausmeter/domain` — die Web-App darf die Domäne nur für Typen benutzen.
 * Sie dient der Auswahl im Dialog und nicht als Schutz: Den Schutz hält die
 * API. Ein Test hält beide über alle bekannten Typen gleich.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { isInverterDevice, isStorageDevice } from "./device-type.js";
import { formatEnergyFull, formatPowerFull } from "./energy-format.js";

type Kind = ApiComponents["schemas"]["GenerationAsset"]["kind"];
type AssetState = ApiComponents["schemas"]["GenerationAssetState"];
type StorageFields = Omit<ApiComponents["schemas"]["StorageState"], "measuredAt">;

/** Eine PV-Anlage braucht einen Wechselrichter, ein Speicher einen Speicher oder einen Wechselrichter. */
export function fitsAssetKind(kind: Kind, deviceType: string): boolean {
  return kind === "pv"
    ? isInverterDevice(deviceType)
    : isStorageDevice(deviceType) || isInverterDevice(deviceType);
}

/** Dieselben Regeln wie überall: kWh mit Stellen nach dem Betrag, Leistung in W oder kW. */
export const formatKwh = (wh: number): string => formatEnergyFull(wh);
export const formatKw = (w: number): string => formatPowerFull(w);

/**
 * Die Zeilen eines Speichers — am EVCC-Speicher, an der Batterie hinter einem
 * Victron GX und an der Batterie eines Hybrids (HM-126), denn alle sprechen
 * denselben Vertrag. **Nur, was gemeldet wurde** (HM-041).
 */
export function storageRows(s: StorageFields): (readonly [string, string])[] {
  const rows: (readonly [string, string])[] = [];
  if (s.soc.datapointId !== null && s.soc.value !== null) {
    rows.push(["Ladezustand", `${Math.round(s.soc.value * 100)} %`]);
  }
  if (s.powerW.datapointId !== null && s.powerW.value !== null) {
    rows.push(["Leistung", storagePowerText(s.powerW.value)]);
  }
  if (s.energyChargedWh.datapointId !== null && s.energyChargedWh.value !== null) {
    rows.push(["Geladen gesamt", formatKwh(s.energyChargedWh.value)]);
  }
  if (s.energyDischargedWh.datapointId !== null && s.energyDischargedWh.value !== null) {
    rows.push(["Entladen gesamt", formatKwh(s.energyDischargedWh.value)]);
  }
  return rows;
}

function storagePowerText(watts: number): string {
  return watts > 0
    ? `${formatKw(watts)} · lädt`
    : watts < 0
      ? `${formatKw(-watts)} · entlädt`
      : `${formatPowerFull(0)} · ruht`;
}

const percent = (ratio: number): string => `${Math.round(ratio * 100)} %`;

/**
 * Was die Anlage gerade meldet, in einer Zeile — oder `null`, wenn es nichts
 * zu sagen gibt. Die PV-Anlage nennt die Leistung **aller** Geräte und, mit
 * Nennleistung, den Anteil daran; der Speicher Ladezustand und Richtung. Bei
 * mehreren Batterien steht der Ladezustand je Gerät da — gemittelt wird er
 * nicht, die API liefert dann keinen für die Anlage (HM-130).
 */
export function assetStateLine(state: AssetState): string | null {
  if (state.pv !== null) {
    const { generationW, utilisation, energyWh } = state.pv;
    if (generationW !== null) {
      return utilisation === null
        ? formatKw(generationW)
        : `${formatKw(generationW)} · ${percent(utilisation)} der Nennleistung`;
    }
    return energyWh === null ? null : `Ertrag gesamt ${formatKwh(energyWh)}`;
  }
  if (state.battery !== null) {
    const { soc, powerW } = state.battery;
    const socs = state.devices.flatMap((device) =>
      device.battery?.soc.value == null ? [] : [device.battery.soc.value],
    );
    const parts = [
      soc !== null ? percent(soc) : socs.length > 0 ? socs.map(percent).join(" / ") : null,
      powerW === null ? null : storagePowerText(powerW),
    ].filter((part): part is string => part !== null);
    return parts.length === 0 ? null : parts.join(" · ");
  }
  return null;
}

/** Warum eine Anlage mit Gerät nichts zeigt — `null`, wenn sie etwas zeigt oder keines hat. */
export function assetStateGap(state: AssetState): string | null {
  if (state.reason === "no_device") return null;
  if (assetStateLine(state) !== null) return null;
  const reasons = state.devices.map((device) => device.reason);
  if (reasons.includes("device_not_for_kind")) return "Ein Gerät passt nicht zur Art.";
  if (reasons.length > 0 && reasons.every((reason) => reason === "part_not_reported")) {
    return reasons.length === 1
      ? "Das Gerät meldet diesen Teil nicht."
      : "Die Geräte melden diesen Teil nicht.";
  }
  /* Eine Summe ohne Summand ist unbekannt — die API nennt dann keine. */
  const reporting = state.devices.filter((device) => device.measuredAt !== null).length;
  if (reporting > 0 && reporting < state.devices.length) return "Noch nicht alle Geräte gemeldet.";
  return "Noch kein Wert gemeldet.";
}
